import { Box, Container, Typography } from "@mui/material";
import { keyframes } from "@mui/system";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const floatY = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-14px); }
`;

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.5; }
  100% { transform: scale(2.4); opacity: 0; }
`;

const slideRight = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

const boxData = [
  {
    icon: "🎯",
    number: "01",
    title: "Client-Centric",
    description:
      "Our team of experts works closely with you to develop customized solutions that reach your clients and achieve your business goals.",
    color: "#5b7cfa",
    light: "rgba(91,124,250,0.08)",
    delay: 0.1,
  },
  {
    icon: "💡",
    number: "02",
    title: "Innovative",
    description:
      "We specialize in innovative solutions that transform the way you do business. Our team delivers creative breakthroughs to keep you ahead.",
    color: "#764ba2",
    light: "rgba(118,75,162,0.08)",
    delay: 0.2,
  },
  {
    icon: "⚡",
    number: "03",
    title: "Transform",
    description:
      "We help modernize your business with cutting-edge software solutions that keep you ahead of the curve and improve productivity.",
    color: "#06b6d4",
    light: "rgba(6,182,212,0.08)",
    delay: 0.3,
  },
];

const AnimatedBoxes = () => {
  return (
    <Box
      sx={{
        width: "100%",
        py: { xs: 8, sm: 10, md: 12 },
        background: "linear-gradient(135deg, #eef2ff 0%, #ede9fe 50%, #fdf4ff 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Dot-grid overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(circle, rgba(91,124,250,0.1) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Soft gradient orbs */}
      <Box sx={{ position: "absolute", top: "-120px", right: "-120px", width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,124,250,0.12) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />
      <Box sx={{ position: "absolute", bottom: "-100px", left: "-100px", width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(118,75,162,0.1) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Heading */}
        <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 8 } }}>
          {/* Label pill */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.5,
              py: 0.8,
              borderRadius: "999px",
              border: "1px solid rgba(91,124,250,0.3)",
              background: "rgba(91,124,250,0.06)",
              mb: 3,
              animation: `${fadeUp} 0.6s ease-out both`,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #5b7cfa, #764ba2)",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  background: "#5b7cfa",
                  animation: `${pulseRing} 1.6s ease-out infinite`,
                },
              }}
            />
            <Typography sx={{ color: "#5b7cfa", fontSize: 12, fontWeight: 700, letterSpacing: 1.5 }}>
              WHY CHOOSE US
            </Typography>
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "30px", sm: "40px", md: "52px" },
              fontWeight: 800,
              letterSpacing: "-1px",
              lineHeight: 1.15,
              mb: 2.5,
              animation: `${fadeUp} 0.7s ease-out 0.1s both`,
              background: "linear-gradient(135deg, #1a1a2e 0%, #5b7cfa 50%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Built Different.
            <br />
            Designed to Deliver.
          </Typography>

          {/* Animated underline */}
          <Box
            sx={{
              width: 80,
              height: 4,
              borderRadius: 2,
              background: "linear-gradient(90deg, #5b7cfa, #764ba2)",
              mx: "auto",
              mb: 3,
              transformOrigin: "left",
              animation: `${slideRight} 0.8s ease-out 0.3s both`,
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "18px", md: "19px" },
              color: "#64748b",
              maxWidth: 600,
              mx: "auto",
              lineHeight: 1.8,
              animation: `${fadeUp} 0.7s ease-out 0.2s both`,
            }}
          >
            We empower businesses with cutting-edge solutions that deliver exceptional, measurable results.
          </Typography>
        </Box>

        {/* Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
            gap: { xs: 3, sm: 4 },
          }}
        >
          {boxData.map((box, index) => (
            <Box
              key={index}
              sx={{
                position: "relative",
                borderRadius: "24px",
                overflow: "hidden",
                background: "#fff",
                border: "1px solid #e8edf5",
                boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
                p: { xs: "28px", sm: "36px" },
                animation: `${fadeUp} 0.7s ease-out ${box.delay}s both`,
                transition: "all 0.4s ease",
                cursor: "default",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: `0 24px 60px ${box.color}22`,
                  border: `1px solid ${box.color}44`,
                },
                "&:hover .svc-bar": {
                  transform: "scaleY(1)",
                },
              }}
            >
              {/* Top accent bar */}
              <Box
                className="svc-bar"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${box.color}, ${box.color}88)`,
                  borderRadius: "24px 24px 0 0",
                  transform: "scaleY(0)",
                  transformOrigin: "top",
                  transition: "transform 0.35s ease",
                }}
              />

              {/* Number watermark */}
              <Typography
                sx={{
                  position: "absolute",
                  top: 14,
                  right: 20,
                  fontSize: "52px",
                  fontWeight: 900,
                  color: `${box.color}14`,
                  lineHeight: 1,
                  userSelect: "none",
                  fontFamily: "monospace",
                  letterSpacing: "-2px",
                }}
              >
                {box.number}
              </Typography>

              {/* Icon */}
              <Box
                sx={{
                  width: { xs: 56, sm: 64 },
                  height: { xs: 56, sm: 64 },
                  borderRadius: "16px",
                  background: box.light,
                  border: `1px solid ${box.color}22`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: { xs: "26px", sm: "30px" },
                  mb: 2.5,
                  animation: `${floatY} 4s ease-in-out infinite ${box.delay + 0.5}s`,
                  transition: "all 0.35s ease",
                  "&:hover": {
                    background: `${box.color}22`,
                  },
                }}
              >
                {box.icon}
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: "20px", sm: "22px" },
                  fontWeight: 700,
                  color: "#1a1a2e",
                  mb: 1.5,
                  transition: "color 0.3s ease",
                  "&:hover": { color: box.color },
                }}
              >
                {box.title}
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "15px", sm: "16px" },
                  color: "#64748b",
                  lineHeight: 1.75,
                }}
              >
                {box.description}
              </Typography>

              {/* Bottom accent */}
              <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 28, height: 2, borderRadius: 1, background: `linear-gradient(90deg, ${box.color}, transparent)` }} />
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: box.color, opacity: 0.5 }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default AnimatedBoxes;
